import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { formatDate } from '../../utils/dateUtils';

const NOTICE_TYPES = [
  { key: 'myvip', label: 'My Community VIP', desc: 'Virtual parking permit via myvipparking.com', color: '#8b5cf6', needsDate: true, needsPasscode: true },
  { key: 'rpp', label: 'Resident Parking Permit', desc: 'Parking permit via residentparkingpermit.com', color: '#3b82f6', needsDate: true, needsPasscode: true },
  { key: 'r2park', label: 'Register2Park', desc: 'Visitor registration via R2park.com', color: '#10b981', needsDate: false, needsPasscode: false },
];

const VERSION_LABELS = { 1: '1st Notice', 2: '2nd Notice', 3: '3rd Notice' };

export default function NoticeGeneratorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountIdParam = searchParams.get('account_id');

  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(null); // 'new' or 'edit'

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Form state
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [formData, setFormData] = useState({
    account_id: null,
    property_name: '',
    property_address: '',
    tow_start_date: '',
    community_passcode: '',
  });

  // Edit mode state
  const [editingNotice, setEditingNotice] = useState(null);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatedNotices, setGeneratedNotices] = useState([]);
  const [error, setError] = useState('');

  // If coming from account, pre-fill
  useEffect(() => {
    if (accountIdParam) {
      setMode('new');
      setStep(2);
      // Try to pre-fill from property info sheet
      api.getPropertyInfoByAccount(accountIdParam).then(info => {
        if (info) {
          setFormData(prev => ({
            ...prev,
            account_id: parseInt(accountIdParam),
            property_name: info.property_name || '',
            property_address: info.property_address || '',
            tow_start_date: info.tow_start_date || '',
            community_passcode: info.password || '',
          }));
        }
      }).catch(() => {});
      // Also try from account itself
      api.getAccount(accountIdParam).then(acct => {
        if (acct) {
          setFormData(prev => ({
            ...prev,
            account_id: parseInt(accountIdParam),
            property_name: prev.property_name || acct.property_name || '',
            property_address: prev.property_address || acct.property_address || '',
          }));
        }
      }).catch(() => {});
    }
  }, [accountIdParam]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      setSearching(true);
      if (mode === 'edit') {
        api.searchNotices(searchQuery).then(setSearchResults).catch(() => setSearchResults([])).finally(() => setSearching(false));
      } else {
        // Search properties table + accounts for pre-fill
        api.searchProperties(searchQuery).then(data => setSearchResults(data || [])).catch(() => setSearchResults([])).finally(() => setSearching(false));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, mode]);

  const handleModeSelect = (m) => {
    setMode(m);
    setStep(2);
    setSearchQuery('');
    setSearchResults([]);
    setError('');
  };

  const handlePropertySelect = (item) => {
    setFormData(prev => ({
      ...prev,
      account_id: item.source === 'account' ? item.id : null,
      property_name: item.property_name || '',
      property_address: item.property_address || '',
    }));
    // If it's an account, try to get property info for more data
    if (item.source === 'account') {
      api.getPropertyInfoByAccount(item.id).then(info => {
        if (info) {
          setFormData(prev => ({
            ...prev,
            property_name: info.property_name || prev.property_name,
            property_address: info.property_address || prev.property_address,
            tow_start_date: info.tow_start_date || prev.tow_start_date,
            community_passcode: info.password || prev.community_passcode,
          }));
        }
      }).catch(() => {});
    }
    setSearchQuery('');
    setSearchResults([]);
    setStep(3);
  };

  const handleEditSelect = (notice) => {
    setEditingNotice(notice);
    setFormData({
      account_id: notice.account_id,
      property_name: notice.property_name,
      property_address: notice.property_address,
      tow_start_date: notice.tow_start_date,
      community_passcode: notice.community_passcode,
    });
    setSelectedTypes([notice.notice_type]);
    setStep(4);
  };

  const toggleType = (key) => {
    setSelectedTypes(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const needsDatePasscode = selectedTypes.some(t => t === 'myvip' || t === 'rpp');

  const handleGenerate = async () => {
    setError('');
    if (!formData.property_name.trim()) return setError('Property name is required');
    if (needsDatePasscode && !formData.tow_start_date) return setError('Tow start date is required');
    if (needsDatePasscode && !formData.community_passcode.trim()) return setError('Community passcode is required');

    setGenerating(true);
    try {
      if (editingNotice) {
        // Update existing notice
        const updated = await api.updateNotice(editingNotice.id, formData);
        setGeneratedNotices([updated]);
      } else {
        // Generate new
        const results = await api.generateNotices({
          ...formData,
          notice_types: selectedTypes,
        });
        setGeneratedNotices(results);
      }
      setStep(5);
    } catch (err) {
      setError(err.message || 'Failed to generate notices');
    }
    setGenerating(false);
  };

  const handleNewVersion = async (notice) => {
    try {
      const created = await api.createNoticeVersion(notice.id);
      setGeneratedNotices(prev => prev.map(n => n.id === notice.id ? { ...n, superseded: true } : n).concat(created));
    } catch (err) {
      setError(err.message || 'Failed to create new version');
    }
  };

  const downloadFile = async (id, format, notice) => {
    try {
      const response = await fetch(`/api/notices/${id}/download/${format}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = NOTICE_TYPES.find(t => t.key === notice.notice_type)?.label || notice.notice_type;
      a.download = `${notice.property_name} - ${label} - v${notice.version}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download file');
    }
  };

  const handleReset = () => {
    setStep(1);
    setMode(null);
    setSelectedTypes([]);
    setFormData({ account_id: null, property_name: '', property_address: '', tow_start_date: '', community_passcode: '' });
    setEditingNotice(null);
    setGeneratedNotices([]);
    setSearchQuery('');
    setSearchResults([]);
    setError('');
  };

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Notice Generator</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {step > 1 && step < 5 && (
            <button className="btn btn-secondary" onClick={handleReset}>Start Over</button>
          )}
          {/* History entry point — moved here when the standalone
              'Notices' sidebar link was retired. */}
          <button className="btn btn-secondary" onClick={() => navigate('/sales/notices/history')}>
            History
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {step > 1 && step < 5 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
          {[2, 3, 4].map(s => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: s <= step ? '#0f172a' : '#e2e8f0',
              transition: 'background 0.3s',
            }} />
          ))}
        </div>
      )}

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          padding: '10px 16px', marginBottom: 16, color: '#dc2626', fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* Step 1: Choose Mode */}
      {step === 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 700, margin: '40px auto' }}>
          <ModeCard
            icon="&#128196;"
            title="Create New Notice"
            desc="Generate new parking notices for a property"
            color="#3b82f6"
            onClick={() => handleModeSelect('new')}
          />
          <ModeCard
            icon="&#9999;&#65039;"
            title="Edit Previous Notice"
            desc="Update and regenerate an existing notice"
            color="#8b5cf6"
            onClick={() => handleModeSelect('edit')}
          />
        </div>
      )}

      {/* Step 2: Property Search */}
      {step === 2 && (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
            {mode === 'edit' ? 'Find Existing Notice' : 'Search Property'}
          </h2>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            {mode === 'edit'
              ? 'Search by property name to find and edit a previous notice'
              : 'Search for an existing account to pre-fill property details, or skip to enter manually'}
          </p>

          <div style={{ position: 'relative', marginBottom: 16 }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={mode === 'edit' ? 'Search property name...' : 'Search by property name...'}
              autoFocus
              style={{
                width: '100%', padding: '12px 16px', fontSize: 15, borderRadius: 10,
                border: '1px solid #d1d5db', boxSizing: 'border-box',
              }}
            />
            {searching && (
              <span style={{ position: 'absolute', right: 14, top: 14, fontSize: 12, color: '#94a3b8' }}>Searching...</span>
            )}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 16 }}>
              {mode === 'edit' ? (
                // Edit mode: show notices grouped by property
                searchResults.map(notice => (
                  <div
                    key={notice.id}
                    onClick={() => handleEditSelect(notice)}
                    style={{
                      padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{notice.property_name}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {NOTICE_TYPES.find(t => t.key === notice.notice_type)?.label || notice.notice_type}
                        {' \u2022 '}{VERSION_LABELS[notice.version]}
                        {' \u2022 '}{formatDate(notice.created_at, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>&#x276F;</span>
                  </div>
                ))
              ) : (
                // New mode: show accounts
                searchResults.slice(0, 8).map(acct => (
                  <div
                    key={acct.id}
                    onClick={() => handlePropertySelect(acct)}
                    style={{
                      padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{acct.property_name}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{acct.property_address || 'No address'}</div>
                    </div>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>&#x276F;</span>
                  </div>
                ))
              )}
            </div>
          )}

          {mode === 'new' && (
            <button
              className="btn btn-secondary"
              onClick={() => setStep(3)}
              style={{ marginTop: 8 }}
            >
              Skip &mdash; Enter Manually
            </button>
          )}
        </div>
      )}

      {/* Step 3: Select Notice Types (new mode only) */}
      {step === 3 && (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Which Notices Are Needed?</h2>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Select all that apply</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
            {NOTICE_TYPES.map(nt => {
              const selected = selectedTypes.includes(nt.key);
              return (
                <div
                  key={nt.key}
                  onClick={() => toggleType(nt.key)}
                  style={{
                    background: '#fff',
                    borderRadius: 12,
                    border: `2px solid ${selected ? nt.color : '#e2e8f0'}`,
                    padding: '16px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: 6,
                    border: `2px solid ${selected ? nt.color : '#d1d5db'}`,
                    background: selected ? nt.color : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, transition: 'all 0.2s',
                  }}>
                    {selected && <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>&#10003;</span>}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{nt.label}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{nt.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
            <button
              className="btn btn-primary"
              disabled={selectedTypes.length === 0}
              onClick={() => setStep(4)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Fields Form */}
      {step === 4 && (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
            {editingNotice ? 'Edit Notice Details' : 'Notice Details'}
          </h2>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
            {editingNotice
              ? `Editing ${NOTICE_TYPES.find(t => t.key === editingNotice.notice_type)?.label} - ${VERSION_LABELS[editingNotice.version]}`
              : 'Fill in the details for your notices'}
          </p>

          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24 }}>
            <FormField label="Property Name" required>
              <input
                type="text"
                value={formData.property_name}
                onChange={e => setFormData(prev => ({ ...prev, property_name: e.target.value }))}
                placeholder="e.g. Sunset Apartments"
                className="sales-input"
              />
            </FormField>

            <FormField label="Property Address">
              <input
                type="text"
                value={formData.property_address}
                onChange={e => setFormData(prev => ({ ...prev, property_address: e.target.value }))}
                placeholder="e.g. 123 Main St, Fort Worth, TX"
                className="sales-input"
              />
            </FormField>

            {needsDatePasscode && (
              <>
                <FormField label="Towing Start Date" required>
                  <input
                    type="date"
                    value={formData.tow_start_date}
                    onChange={e => setFormData(prev => ({ ...prev, tow_start_date: e.target.value }))}
                    className="sales-input"
                  />
                </FormField>

                <FormField label="Community Passcode" required>
                  <input
                    type="text"
                    value={formData.community_passcode}
                    onChange={e => setFormData(prev => ({ ...prev, community_passcode: e.target.value }))}
                    placeholder="e.g. SUNSET2026"
                    className="sales-input"
                  />
                </FormField>
              </>
            )}

            {/* Selected types summary */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                Generating
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(editingNotice ? [editingNotice.notice_type] : selectedTypes).map(key => {
                  const nt = NOTICE_TYPES.find(t => t.key === key);
                  return (
                    <span key={key} style={{
                      display: 'inline-block', padding: '4px 12px', borderRadius: 20,
                      fontSize: 12, fontWeight: 600, background: `${nt?.color || '#94a3b8'}18`, color: nt?.color || '#94a3b8',
                    }}>
                      {nt?.label || key}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            {!editingNotice && <button className="btn btn-secondary" onClick={() => setStep(3)}>Back</button>}
            {editingNotice && <button className="btn btn-secondary" onClick={handleReset}>Cancel</button>}
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? 'Generating...' : editingNotice ? 'Update & Regenerate' : 'Generate Notices'}
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Results */}
      {step === 5 && (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>&#9989;</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
              {editingNotice ? 'Notice Updated!' : 'Notices Generated!'}
            </h2>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              Download your files below in Word or PDF format
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {generatedNotices.map(notice => {
              const nt = NOTICE_TYPES.find(t => t.key === notice.notice_type);
              return (
                <div key={notice.id} style={{
                  background: notice.superseded ? '#f8fafc' : '#fff',
                  borderRadius: 14,
                  border: `1px solid ${notice.superseded ? '#f1f5f9' : '#e2e8f0'}`,
                  padding: 20,
                  opacity: notice.superseded ? 0.5 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 20,
                          fontSize: 11, fontWeight: 700, background: `${nt?.color || '#94a3b8'}18`, color: nt?.color || '#94a3b8',
                        }}>
                          {nt?.label || notice.notice_type}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                          {VERSION_LABELS[notice.version]}
                        </span>
                        {notice.superseded && (
                          <span style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>Superseded</span>
                        )}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginTop: 6 }}>
                        {notice.property_name}
                      </div>
                    </div>
                    {!notice.superseded && (notice.version || 1) < 3 && (
                      <button
                        onClick={() => handleNewVersion(notice)}
                        style={{
                          fontSize: 12, fontWeight: 600, color: '#8b5cf6', background: '#8b5cf618',
                          border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                        }}
                      >
                        Create {VERSION_LABELS[(notice.version || 1) + 1]}
                      </button>
                    )}
                  </div>

                  {!notice.superseded && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        onClick={() => downloadFile(notice.id, 'docx', notice)}
                        style={{
                          flex: 1, textAlign: 'center', padding: '10px 16px', borderRadius: 8,
                          background: '#0f172a', color: '#fff', fontWeight: 600, fontSize: 13,
                          border: 'none', cursor: 'pointer',
                        }}
                      >
                        Download Word (.docx)
                      </button>
                      <button
                        onClick={() => downloadFile(notice.id, 'pdf', notice)}
                        style={{
                          flex: 1, textAlign: 'center', padding: '10px 16px', borderRadius: 8,
                          background: '#fff', color: '#0f172a', fontWeight: 600, fontSize: 13,
                          border: '1px solid #d1d5db', cursor: 'pointer',
                        }}
                      >
                        Download PDF
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginTop: 28 }}>
            <button className="btn btn-secondary" onClick={handleReset}>
              Generate Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helper Components ────────────────────────────────────────

function ModeCard({ icon, title, desc, color, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0',
        padding: 32, cursor: 'pointer', textAlign: 'center',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      <div style={{ fontSize: 40, marginBottom: 12 }} dangerouslySetInnerHTML={{ __html: icon }} />
      <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
        {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

